import { Component, inject, OnInit } from '@angular/core';
import { DialogService } from '@shared/services/dialog.service';
import { MatIcon } from '@angular/material/icon';
import { MatTooltip } from '@angular/material/tooltip';
import {
  DataTable,
  FilterOption,
  FilterSelect,
  FiltersContainer,
  TableCellDirective,
  TableColumn,
} from '@shared/components/ui/list-components';

import { PageHeaderComponent } from '../../../../../shared/components/common/page-header/page-header.component';
import { ErrorService } from '../../../../../shared/services/error.service';
import { Cliente } from '../../crm/clientes/clientes.interface';
import { DetalleStockToro } from './detalle-stock-toro/detalle-stock-toro';
import { RecepcionSemen } from './recepcion-semen/recepcion-semen';
import { SemenService } from './semen.service';
import { agruparStockPorToro, StockToro } from './stock-semen.util';

@Component({
  selector: 'app-stock-semen',
  imports: [
    DataTable,
    FilterSelect,
    FiltersContainer,
    MatIcon,
    MatTooltip,
    PageHeaderComponent,
    TableCellDirective,
  ],
  templateUrl: './stock-semen.html',
})
export class StockSemen implements OnInit {
  loading = false;
  stock: StockToro[] = [];
  /** Fila de totales al pie: solo con un cliente filtrado. */
  totales: Record<string, unknown> | null = null;
  clientes: Cliente[] = [];
  clienteId = '';

  // Una fila por cliente y toro: lo que hay en el rack, sumando todas las recepciones
  columns: TableColumn[] = [
    { key: 'cliente', header: 'Cliente' },
    { key: 'toro', header: 'Toro' },
    { key: 'partidas', header: 'Partidas' },
    { key: 'entraron', header: 'Entraron' },
    { key: 'usadas', header: 'Usadas' },
    { key: 'quedan', header: 'Quedan' },
    { key: 'acciones', header: '' },
  ];

  get clientesOptions(): FilterOption[] {
    return this.clientes.map(cliente => ({ value: cliente.id, label: cliente.persona.nombre_generico }));
  }

  private semenService = inject(SemenService);
  private errorService = inject(ErrorService);
  private dialogService = inject(DialogService);

  ngOnInit(): void {
    this.loadClientes();
    this.getStock();
  }

  loadClientes(): void {
    this.semenService.comboClientes().subscribe({
      next: (response) => {
        if (response.status) {
          this.clientes = response.data;
        }
      },
      error: (error) => {
        this.errorService.handleError(error);
      },
    });
  }

  getStock(): void {
    this.loading = true;
    this.semenService.getSemenesEntradas({
      cli_cliente_id: this.clienteId ? Number(this.clienteId) : undefined,
    }).subscribe({
      next: (response) => {
        if (response.status) {
          this.stock = agruparStockPorToro(response.data);
          this.totales = this.clienteId ? sumarStock(this.stock) : null;
        }
      },
      error: (error) => {
        this.errorService.handleError(error);
      },
    }).add(() => {
      this.loading = false;
    });
  }

  onClienteChange(value: string | number): void {
    this.clienteId = String(value);
    this.getStock();
  }

  get hasActiveFilters(): boolean {
    return !!this.clienteId;
  }

  clearFilters(): void {
    this.clienteId = '';
    this.getStock();
  }

  openRecepcion(): void {
    this.dialogService.openForm(RecepcionSemen, {
      size: 'xl',
      data: { clienteId: this.clienteId ? Number(this.clienteId) : null },
      config: { maxHeight: '90vh' },
    }).afterClosed().subscribe(result => {
      if (result) {
        this.getStock();
      }
    });
  }

  openDetalle(grupo: StockToro): void {
    this.dialogService.openForm(DetalleStockToro, {
      size: 'xl',
      data: { clienteId: grupo.cli_cliente_id, toroId: grupo.toro_id },
      config: { maxHeight: '90vh' },
    }).afterClosed().subscribe(result => {
      if (result) {
        this.getStock();
      }
    });
  }

  partidasTexto(grupo: StockToro): string {
    return grupo.partidas.map(partida => partida.partida).join(', ') || '-';
  }
}

function sumarStock(stock: StockToro[]): Record<string, unknown> {
  const suma = (campo: 'entraron' | 'usadas' | 'quedan') => stock.reduce((total, grupo) => total + grupo[campo], 0);

  return { cliente: 'Total', entraron: suma('entraron'), usadas: suma('usadas'), quedan: suma('quedan') };
}
